// 全局类型与默认参数：默认值对齐参考实现（一键水印 v1.0 by_yfjm）

export type RGBA = [number, number, number, number];

export type Anchor =
  | "top-left"
  | "top-center"
  | "top-right"
  | "middle-left"
  | "center"
  | "middle-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

/** 定位方式：九宫格 + 偏移，或自由坐标（在预览里拖动后自动切换） */
export type PositionMode = "anchor" | "free";

/** 绘制参数（与具体平台无关，UI / Worker 共用） */
export interface Settings {
  text: string;
  fontFamily: string;
  /** 字号占图片高度的比例 */
  fontSizeRatio: number;
  textColor: RGBA;
  strokeColor: RGBA;
  /** 描边宽度占图片高度的比例 */
  strokeWidthRatio: number;
  boxColor: RGBA;
  boxPaddingRatio: number;
  boxRadiusRatio: number;
  anchor: Anchor;
  offsetX: number;
  offsetY: number;
  positionMode: PositionMode;
  /** 自由坐标：水印块左上角在「可移动范围」里的比例（0=最左/最上，1=最右/最下） */
  freeX: number;
  freeY: number;
  maskEnabled: boolean;
  /** 遮挡矩形（起 X、起 Y、止 X、止 Y，均为图片宽高比例） */
  maskRect: [number, number, number, number];
  maskColor: RGBA;
  logoScaleRatio: number;
  /** 0~255 */
  logoOpacity: number;
  /** 平铺水印（默认关；开启后铺满整图，且不画背景框） */
  tileEnabled: boolean;
  /** 平铺旋转角度（度） */
  tileAngle: number;
  /** 平铺间距占图片高度的比例 */
  tileGapRatio: number;
}

/** 处理管线选项 */
export interface ProcessOptions {
  /** JPEG 编码质量 0~1 */
  jpegQuality: number;
  /** 版权署名：写进 EXIF Copyright/Artist、JPEG 注释段、PNG tEXt；留空则不写 */
  copyright: string;
}

export const DEFAULT_SETTINGS: Settings = {
  text: "",
  fontFamily: "Microsoft YaHei",
  fontSizeRatio: 0.025,
  textColor: [255, 255, 255, 72],
  strokeColor: [0, 110, 220, 255],
  strokeWidthRatio: 0.0015,
  boxColor: [0, 0, 0, 110],
  boxPaddingRatio: 0.008,
  boxRadiusRatio: 0.005,
  anchor: "bottom-left",
  offsetX: 0.03,
  offsetY: 0.03,
  positionMode: "anchor",
  freeX: 0,
  freeY: 1,
  maskEnabled: false,
  maskRect: [0.0, 0.862, 0.66, 0.982],
  maskColor: [0, 0, 0, 255],
  logoScaleRatio: 0.04,
  logoOpacity: 200,
  tileEnabled: false,
  tileAngle: -30,
  tileGapRatio: 0.08,
};

export const DEFAULT_OPTIONS: ProcessOptions = { jpegQuality: 0.995, copyright: "" };

/** 可扫描的输入扩展名 */
export const IMAGE_EXTENSIONS = new Set([
  ".jpg", ".jpeg", ".jpe", ".png", ".webp", ".bmp", ".tif", ".tiff", ".heic", ".heif",
]);

/** 浏览器 Canvas 能直接编码的格式 */
const ENCODABLE: Record<string, { mime: string; ext: string }> = {
  ".jpg": { mime: "image/jpeg", ext: ".jpg" },
  ".jpeg": { mime: "image/jpeg", ext: ".jpg" },
  ".jpe": { mime: "image/jpeg", ext: ".jpg" },
  ".png": { mime: "image/png", ext: ".png" },
  ".webp": { mime: "image/webp", ext: ".webp" },
};

/** 由源文件扩展名决定输出格式；无法编码的格式统一回落为 PNG */
export function outputFormatFor(ext: string): { mime: string; ext: string } {
  return ENCODABLE[ext.toLowerCase()] ?? { mime: "image/png", ext: ".png" };
}

/** 常见图片 MIME（用于把 ArrayBuffer 包成 Blob 解码） */
export function mimeFor(ext: string): string {
  switch (ext.toLowerCase()) {
    case ".jpg":
    case ".jpeg":
    case ".jpe":
      return "image/jpeg";
    case ".png":
      return "image/png";
    case ".webp":
      return "image/webp";
    case ".bmp":
      return "image/bmp";
    case ".tif":
    case ".tiff":
      return "image/tiff";
    case ".heic":
      return "image/heic";
    case ".heif":
      return "image/heif";
    default:
      return "application/octet-stream";
  }
}
